import React from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2 } from 'lucide-react';
import styles from './ServiceTimeline.module.css';

export interface TimelineStep {
    phase: string;
    duration: string;
    outputs: string[];
}

interface ServiceTimelineProps {
    title?: string;
    steps: TimelineStep[];
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const stepVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.15 },
    },
};

export const ServiceTimeline: React.FC<ServiceTimelineProps> = ({ title, steps }) => {
    return (
        <section className={styles.timeline}>
            {title && <h2 className={styles.timeline__title}>{title}</h2>}

            <motion.div
                className={styles.timeline__container}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
            >
                {steps.map((step, index) => {
                    const isLast = index === steps.length - 1;

                    return (
                        <motion.div
                            key={index}
                            className={styles.timeline__step}
                            variants={stepVariants}
                        >
                            {/* Connector Line */}
                            {!isLast && (
                                <div className={styles.timeline__connector}>
                                    <motion.div
                                        className={styles.timeline__connectorLine}
                                        initial={{ scaleY: 0 }}
                                        whileInView={{ scaleY: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.15, delay: 0 + index * 0.15 }}
                                    />
                                </div>
                            )}

                            {/* Step Marker */}
                            <div className={styles.timeline__marker}>
                                <motion.div
                                    className={styles.timeline__markerNum}
                                    whileHover={{ scale: 1.1 }}
                                >
                                    <span>{String(index + 1).padStart(2, '0')}</span>
                                    <div className={styles.timeline__markerGlow} />
                                </motion.div>
                            </div>

                            {/* Step Content */}
                            <div className={styles.timeline__content}>
                                <div className={styles.timeline__header}>
                                    <h3 className={styles.timeline__phase}>{step.phase}</h3>
                                    <span className={styles.timeline__duration}>
                                        <Clock size={14} />
                                        {step.duration}
                                    </span>
                                </div>

                                <ul className={styles.timeline__outputs}>
                                    {step.outputs.map((output, outputIndex) => (
                                        <motion.li
                                            key={outputIndex}
                                            className={styles.timeline__outputItem}
                                            initial={{ opacity: 0, x: -10 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0 + index * 0.1 + outputIndex * 0.05 }}
                                        >
                                            <CheckCircle2 size={16} className={styles.timeline__checkIcon} />
                                            {output}
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
        </section>
    );
};

export default ServiceTimeline;
